import { useEffect, useState } from "react";
import { FormControlLabel, Paper, Switch, Typography } from "@material-ui/core";
import { useHistory } from "react-router-dom";
import useReplayConsent from "./use-replay-consent";
import { turnRecordingOff, turnRecordingOn } from "../../utils/replay-document";
import "./replay-consent.scss";

/*
 * Settings > "Help improve QLearn": the one place the session-recording choice
 * lives (owner request, 2026-10-28: no recording notice and no "Privacy
 * choices" control anywhere in the study UI). Renders nothing when recording is
 * not built in (flag off, the iPhone app, the third-party kill switch).
 *
 * The switch does what the old panel's Turn off / Turn on did:
 *   - off stores the refusal in this browser (kept through sign-out), removes
 *     Hotjar's and Contentsquare's cookies and storage, and every open
 *     recording tab stops (replay-document.js turnRecordingOff);
 *   - on stores "allowed"; Settings is never recorded, so recording starts the
 *     next time QLearn is opened (turnRecordingOn).
 * With Global Privacy Control or Do Not Track the switch is off and cannot be
 * turned on: it stays focusable with aria-disabled, so a keyboard or screen
 * reader user still reaches it and hears the reason, and activating it does
 * nothing. Where the default is off (a European or UK time zone, nothing
 * stored) it says so. The whole row (label and switch) is the tap target.
 * Nothing is tracked: a privacy choice is not reported to any analytics vendor.
 */
export const RECORDING_SECTION_ID = "help-improve-qlearn";
const HELP_ID = "settings-recording-help";
const STATUS_ID = "settings-recording-status";

const statusText = (view, changed) => {
  if (view.gpc) return "Off because your browser sends Global Privacy Control.";
  if (view.dnt) return "Off because your browser sends Do Not Track.";
  if (view.on) {
    return changed === "on"
      ? "On. Recording starts the next time you open QLearn."
      : "On in this browser.";
  }
  if (changed === "off") return "Off. Recording has stopped in this browser.";
  if (view.regionDefaultOff) return "Off by default where you are. Turn it on if you'd like to help.";
  return "Off in this browser.";
};

// `layoutKey`: the page passes a value that changes while the content above
// the section is still loading (the signed-in preferences panel), so a visit to
// #help-improve-qlearn lands on the section once that content has its height.
export default function RecordingSettingSection({ layoutKey = "" }) {
  const history = useHistory();
  const view = useReplayConsent();
  // What this visit changed, for the status line only.
  const [changed, setChanged] = useState(null);

  // The privacy policy's 2.4 links to /user-settings#help-improve-qlearn. The
  // section renders after the browser has tried the fragment, so scroll to it,
  // and again each time the content above it changes height while loading.
  useEffect(() => {
    if (!view.available) return;
    try {
      if (window.location.hash !== `#${RECORDING_SECTION_ID}`) return;
      const section = document.getElementById(RECORDING_SECTION_ID);
      if (section && typeof section.scrollIntoView === "function") section.scrollIntoView();
    } catch (e) {
      // Scrolling is a convenience only.
    }
  }, [view.available, layoutKey]);

  if (!view.available) return null;

  // GPC or Do Not Track: off, and nothing turns it on.
  const locked = Boolean(view.gpc || view.dnt);

  const onChange = (event) => {
    if (locked) return;
    if (event.target.checked) {
      turnRecordingOn({ routerHistory: history });
      setChanged("on");
      return;
    }
    // True only on a page whose recorder runs (never Settings): it reloads.
    if (!turnRecordingOff()) setChanged("off");
  };

  return (
    <Paper
      aria-labelledby="settings-recording-title"
      className="settings-paper-container settings-recording-paper"
      component="section"
      data-testid="settings-recording"
      elevation={2}
      id={RECORDING_SECTION_ID}
    >
      <Typography gutterBottom id="settings-recording-title" variant="h6">
        Help improve QLearn
      </Typography>
      <FormControlLabel
        className={`settings-recording-row${locked ? " settings-recording-row-locked" : ""}`}
        control={(
          <Switch
            checked={locked ? false : view.on}
            color="primary"
            inputProps={{
              "aria-describedby": `${STATUS_ID} ${HELP_ID}`,
              ...(locked ? { "aria-disabled": "true" } : {}),
            }}
            name="sessionRecording"
            onChange={onChange}
          />
        )}
        label="Quality improvement: session recordings"
        labelPlacement="start"
      />
      <Typography
        aria-live="polite"
        className="settings-recording-status"
        data-testid="settings-recording-status"
        id={STATUS_ID}
        role="status"
        variant="body2"
      >
        {statusText(view, changed)}
      </Typography>
      <Typography className="settings-recording-help" id={HELP_ID} variant="body2">
        To find what&apos;s confusing, we use Hotjar to record how QLearn&apos;s pages are used: what the
        page shows, and your taps, clicks, scrolling and answer choices. Anything you type is hidden
        before it leaves your device, your name and email are hidden, and recordings aren&apos;t linked
        to your account. This choice applies to this browser. Turning it off takes effect right away; turning it on
        takes effect the next time you open QLearn.
      </Typography>
      {/* A new tab, so the visitor keeps this page as it is. */}
      <a
        className="settings-recording-link"
        href="/privacy-policy#session-recording"
        rel="noopener noreferrer"
        target="_blank"
      >
        Session recording in our privacy policy
        <span className="replay-visually-hidden"> (opens in a new tab)</span>
      </a>
    </Paper>
  );
}
